import { api, type UserState } from "../api";
import { getReferralCode } from "./tracking";

export interface Identity {
  token: string;
  tagId: string;
  identifier: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export let identity: Identity | undefined;

let userState: Promise<UserState | null> | undefined;

export const loadToken = (data: {
  userToken: string;
}): Promise<UserState | null> => {
  const errPrefix = "Invalid token format: ";
  const parts = data.userToken.split(".");
  if (parts.length !== 3) {
    throw new Error(errPrefix + "Missing JWT parts");
  }

  let payload;

  try {
    payload = JSON.parse(atob(parts[1]!)) as Record<string, unknown>;
  } catch {
    throw new Error(errPrefix + "Invalid JWT payload");
  }

  if (!payload?.sub || !payload.aud) {
    throw new Error(errPrefix + "Missing data in JWT payload");
  }

  const prevTagId = identity?.tagId;

  identity = {
    token: data.userToken,
    tagId: String(payload.aud as string),
    identifier: String(payload.sub as string),
    email: parseString(payload.em),
    firstName: parseString(payload.fn),
    lastName: parseString(payload.ln),
  };

  if (!userState || prevTagId !== identity.tagId) {
    userState = api.getUserState({
      tagId: identity.tagId,
      userIdentifier: identity.identifier,
      token: identity.token,
      referralCode: getReferralCode(),
    });
    userState.catch(() => {
      userState = undefined;
    });
  }

  return userState;
};

export const reloadUserState = async (): Promise<UserState | null> => {
  if (!identity) {
    return userState ?? null;
  }

  userState = api.getUserState(
    {
      tagId: identity.tagId,
      userIdentifier: identity.identifier,
      token: identity.token,
      referralCode: getReferralCode(),
    },
    {
      reload: true,
    },
  );
  userState.catch(() => {
    userState = undefined;
  });

  return userState;
};

const parseString = (str: unknown) => (str ? String(str as string) : undefined);
